import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, Clock, Star, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const T = {
  primary:      '#2F9F87',
  primaryDark:  '#1e7a66',
  primaryLight: '#e8f7f4',
  primaryMid:   '#b2e3da',
  border:       '#d0ece7',
  bg:           '#f5faf9',
  white:        '#ffffff',
  text:         '#1a2e2b',
  muted:        '#6b8f89',
  hover:        '#e0f4f0',
  selected:     '#c5ece4',
};

const menuItems = [
  { id: 'overview',           title: 'Overview',                      path: '/',                    icon: '📊', category: 'Main',        type: 'menu' },
  { id: 'dashboard',          title: 'Dashboard',                     path: '/dashboard',           icon: '🏠', category: 'Main',        type: 'menu' },
  { id: 'administration',     title: 'Administration',                path: '/administration',      icon: '🛡️', category: 'Settings',    type: 'menu' },
  { id: 'ship-warnings',      title: 'Ship Warnings & AI',            path: '/ship-warnings',       icon: '⚠️', category: 'Ships',       type: 'menu' },
  { id: 'fleet-tracking',     title: 'Fleet Tracking',                path: '/map-location',        icon: '📍', category: 'Ships',       type: 'menu' },
  { id: 'chart-reports',      title: 'Chart Reports',                 path: '/charts/DAILY',        icon: '📈', category: 'Reports',     type: 'menu' },
  { id: 'processed-reports',  title: 'Processed Reports',             path: '/process-data',        icon: '📋', category: 'Reports',     type: 'menu' },
  { id: 'daily-report-calendar', title: 'Daily Report Calendar',     path: '/daily-report-calendar', icon: '📅', category: 'Reports',   type: 'menu' },
  { id: 'data-workbench',     title: 'Data Workbench',                path: '/user-data',           icon: '💼', category: 'Data',        type: 'menu' },
  { id: 'hull-maintenance',   title: 'Hull Maintenance',              path: '/hull-data',           icon: '🔧', category: 'Maintenance', type: 'menu' },
  { id: 'engine-predictive',  title: 'Engine Predictive Maintenance', path: '/engine-predictives',  icon: '⚙️', category: 'Maintenance', type: 'menu' },
  { id: 'data-analytics',     title: 'Data Analytics',                path: '/data-analytics',      icon: '📊', category: 'Data',        type: 'menu' },
  { id: 'benchmark',          title: 'Benchmark',                     path: '/benchmarks',          icon: '📈', category: 'Data',        type: 'menu' },
];

// ── Sub-components ─────────────────────────────────────────────────────────

const SearchItem = ({ item, isSelected, onSelect, isFavorite, onToggleFavorite }) => (
  <div
    onClick={() => onSelect(item)}
    style={{
      padding: '7px 10px',
      borderRadius: 7,
      cursor: 'pointer',
      background: isSelected ? T.selected : 'transparent',
      border: `1px solid ${isSelected ? T.primaryMid : 'transparent'}`,
      marginBottom: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = T.hover; }}
    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
      <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.title}
        </div>
        <div style={{ color: T.muted, fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.type === 'chart' && item.topic ? item.topic : item.category}
        </div>
      </div>
    </div>
    <button
      onClick={(e) => onToggleFavorite(item, e)}
      style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
    >
      <Star size={13} color={isFavorite ? '#f59e0b' : T.muted} fill={isFavorite ? '#f59e0b' : 'none'} />
    </button>
  </div>
);

const SidebarItem = ({ item, onSelect, isFavorite, onToggleFavorite }) => (
  <div
    onClick={() => onSelect(item)}
    style={{
      padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
      marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8,
      transition: 'all 0.15s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = T.hover; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ fontSize: 13, flexShrink: 0 }}>{item.icon}</span>
    <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {item.title}
    </div>
    <Star size={11} color={isFavorite ? '#f59e0b' : 'transparent'} fill={isFavorite ? '#f59e0b' : 'none'} style={{ flexShrink: 0 }} />
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────

const GlobalSearch = ({ chartTitles = {} }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen]           = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTab, setActiveTab]     = useState('menus');
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [allSearchableItems, setAllSearchableItems] = useState([]);

  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem('search-recent')) || []; } catch { return []; }
  });
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('search-favorites')) || []; } catch { return []; }
  });

  const searchInputRef = useRef(null);

  useEffect(() => { localStorage.setItem('search-recent', JSON.stringify(recentSearches)); }, [recentSearches]);
  useEffect(() => { localStorage.setItem('search-favorites', JSON.stringify(favorites)); }, [favorites]);

  // Build chart items
  useEffect(() => {
    const chartItems = [];
    Object.entries(chartTitles).forEach(([period, charts]) => {
      charts.forEach((chart, index) => {
        chartItems.push({
          id: `chart-${period}-${chart.title}-${index}`,
          title: chart.title,
          topic: chart.topic || 'Uncategorized',
          path: `/management/charts/${period}/${encodeURIComponent(chart.topic || chart.title)}`,
          scrollToTitle: chart.title,
          icon: '📊',
          category: `${period} Charts`,
          type: 'chart',
          period,
        });
      });
    });
    setAllSearchableItems([...menuItems, ...chartItems]);
  }, [chartTitles]);

  // Keyboard shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setIsOpen(true); }
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus on open
  useEffect(() => { if (isOpen) setTimeout(() => searchInputRef.current?.focus(), 50); }, [isOpen]);

  // Filter
  useEffect(() => {
    if (!searchQuery.trim()) { setFilteredItems([]); setSelectedIndex(0); return; }
    const q = searchQuery.toLowerCase();
    let items = allSearchableItems;
    if (activeTab === 'menus')  items = items.filter(i => i.type === 'menu');
    if (activeTab === 'charts') items = items.filter(i => i.type === 'chart');
    setFilteredItems(items.filter(i =>
      i.title.toLowerCase().includes(q) ||
      i.category.toLowerCase().includes(q) ||
      (i.topic && i.topic.toLowerCase().includes(q)) ||
      (i.period && i.period.toLowerCase().includes(q))
    ));
    setSelectedIndex(0);
  }, [searchQuery, allSearchableItems, activeTab]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => Math.min(p + 1, filteredItems.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter' && filteredItems.length > 0) { e.preventDefault(); handleNavigate(filteredItems[selectedIndex]); }
  };

  const scrollToChart = (chartTitle) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-chart-title="${chartTitle}"]`) ||
        Array.from(document.querySelectorAll('h3,h4,.chart-title')).find(el => el.textContent.trim() === chartTitle);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.style.transition = 'background-color 0.5s';
        el.style.backgroundColor = `${T.primaryLight}`;
        setTimeout(() => { el.style.backgroundColor = ''; }, 2000);
      }
    }, 300);
  };

  const handleNavigate = (item) => {
    setRecentSearches(prev => [item, ...prev.filter(i => i.id !== item.id)].slice(0, 5));
    if (item.type === 'chart') { navigate(item.path); if (item.scrollToTitle) scrollToChart(item.scrollToTitle); }
    else navigate(`/management${item.path}`);
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleFavorite = (item, e) => {
    e.stopPropagation();
    setFavorites(prev => prev.some(f => f.id === item.id) ? prev.filter(f => f.id !== item.id) : [...prev, item]);
  };

  const isFavorite = (id) => favorites.some(f => f.id === id);
  const togglePeriod = (p) => setExpandedPeriods(prev => ({ ...prev, [p]: !prev[p] }));
  const chartsByPeriod = Object.entries(chartTitles).reduce((acc, [p, c]) => { acc[p] = c; return acc; }, {});

  // ── Tab button ───────────────────────────────────────────────────────────
  const Tab = ({ value, label }) => (
    <button
      onClick={() => setActiveTab(value)}
      style={{
        padding: '5px 14px',
        borderRadius: 6,
        border: `1px solid ${activeTab === value ? T.primary : T.border}`,
        background: activeTab === value ? T.primary : 'transparent',
        color: activeTab === value ? T.white : T.muted,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          border: `1.5px solid #99dec7`, borderRadius: 15,
          padding: '0 12px',
          height: 37,                  /* fits comfortably inside 54px header */
          width: '28vw', maxWidth: 400, minWidth: 160,
           boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          flexShrink: 0,              /* won't squish when header items crowd */
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = T.primaryMid;
          e.currentTarget.style.boxShadow = `0 0 0 3px ${T.primaryLight}`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
        }}
      >
        <Search size={13} color="#94a3b8" style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: 'left', fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Search ...
        </span>
        <kbd style={{
          background: T.white,
          padding: '1px 6px', borderRadius: 5,
          color: T.primary, fontSize: 11, fontWeight: 600,
          border: `1px solid ${T.border}`,
          letterSpacing: 0.3,
          whiteSpace: 'nowrap',
          lineHeight: '18px',
        }}>
          Ctrl+K
        </kbd>
      </button>

      {/* Modal */}
      {isOpen && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)',
            zIndex: 999999, display: 'flex',
            alignItems: 'flex-start', justifyContent: 'center', paddingTop: '8vh',
          }}
          onClick={() => setIsOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.white, borderRadius: 12,
              width: '90%', maxWidth: 860, maxHeight: '80vh',
              boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
              border: `1px solid ${T.border}`,
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              fontFamily: 'system-ui, sans-serif', fontSize: 13,
            }}
          >
            {/* Search bar */}
            <div style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', gap: 10,
              background: T.bg,
            }}>
              <Search size={16} color={T.primary} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search menus, pages, charts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                style={{
                  flex: 1, background: 'transparent', border: 'none',
                  outline: 'none', color: T.text, fontSize: 13,
                }}
              />
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: T.primaryLight, border: `1px solid ${T.border}`,
                  borderRadius: 5, padding: '2px 8px',
                  color: T.primary, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                }}
              >
                ESC
              </button>
            </div>

            {/* Tabs */}
            <div style={{
              padding: '10px 16px',
              borderBottom: `1px solid ${T.border}`,
              display: 'flex', gap: 6, background: T.white,
            }}>
              <Tab value="menus"  label="Menus"  />
              <Tab value="charts" label="Charts" />
            </div>

            {/* Two-column body */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

              {/* Main results */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 12, borderRight: `1px solid ${T.border}` }}>

                {/* Empty query — Menus tab */}
                {searchQuery === '' && activeTab === 'menus' && menuItems.map(item => (
                  <SearchItem
                    key={item.id} item={item}
                    onSelect={handleNavigate}
                    isFavorite={isFavorite(item.id)}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}

                {/* Empty query — Charts tab */}
                {searchQuery === '' && activeTab === 'charts' && Object.entries(chartsByPeriod).map(([period, charts]) => (
                  <div key={period} style={{ marginBottom: 12 }}>
                    <button
                      onClick={() => togglePeriod(period)}
                      style={{
                        width: '100%', padding: '7px 12px',
                        background: T.primaryLight, border: `1px solid ${T.border}`,
                        borderRadius: 7, color: T.primaryDark, fontSize: 13, fontWeight: 600,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      <span>📊 {period} Charts ({charts.length})</span>
                      <ChevronDown size={14} style={{ transform: expandedPeriods[period] ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    {expandedPeriods[period] && (
                      <div style={{ marginTop: 6, paddingLeft: 8 }}>
                        {charts.map((chart, idx) => {
                          const chartItem = allSearchableItems.find(i => i.period === period && i.title === chart.title);
                          return chartItem ? (
                            <SearchItem
                              key={idx} item={chartItem}
                              onSelect={handleNavigate}
                              isFavorite={isFavorite(chartItem.id)}
                              onToggleFavorite={toggleFavorite}
                            />
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {/* Search results */}
                {filteredItems.length > 0 && filteredItems.map((item, idx) => (
                  <SearchItem
                    key={item.id} item={item}
                    isSelected={idx === selectedIndex}
                    onSelect={handleNavigate}
                    isFavorite={isFavorite(item.id)}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}

                {searchQuery !== '' && filteredItems.length === 0 && (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: T.muted, fontSize: 13 }}>
                    No results found for "{searchQuery}"
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div style={{ width: 260, overflowY: 'auto', padding: 12, background: T.bg }}>

                {recentSearches.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: T.muted, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Clock size={11} /> Recent
                      </span>
                      <button
                        onClick={() => setRecentSearches([])}
                        style={{ background: 'transparent', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 11, padding: '1px 5px', borderRadius: 4 }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = T.hover; e.currentTarget.style.color = T.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.muted; }}
                      >
                        Clear
                      </button>
                    </div>
                    {recentSearches.map(item => (
                      <SidebarItem key={item.id} item={item} onSelect={handleNavigate} isFavorite={isFavorite(item.id)} onToggleFavorite={toggleFavorite} />
                    ))}
                  </div>
                )}

                {favorites.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Star size={11} /> Favorites
                    </div>
                    {favorites.map(item => (
                      <SidebarItem key={item.id} item={item} onSelect={handleNavigate} isFavorite={true} onToggleFavorite={toggleFavorite} />
                    ))}
                  </div>
                )}

                {recentSearches.length === 0 && favorites.length === 0 && (
                  <div style={{ color: T.muted, fontSize: 12, textAlign: 'center', marginTop: 24 }}>
                    Your recent searches<br />and favorites appear here
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '8px 16px', borderTop: `1px solid ${T.border}`,
              display: 'flex', gap: 14, fontSize: 11, color: T.muted,
              background: T.bg, alignItems: 'center',
            }}>
              {[['↑↓', 'navigate'], ['↵', 'select'], ['ESC', 'close']].map(([key, label]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <kbd style={{ background: T.primaryLight, padding: '1px 5px', borderRadius: 3, color: T.primary, fontSize: 10, fontWeight: 600, border: `1px solid ${T.border}` }}>
                    {key}
                  </kbd>
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;